// src/LocatorMap.tsx
import L from "leaflet";
import "leaflet-draw"; // attaches draw controls to L
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet/dist/leaflet.css";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  GeoJSON,
  MapContainer,
  TileLayer,
  WMSTileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import FeatureInfoPopup from "./FeatureInfoPopup";
import styles from "./index.module.scss";
import {
  getLayersForRegion,
  LAYER_STYLES,
  getDefaultVisibleLayerKeys,
  buildWMSUrl,
  buildWMSTileLayerUrl,
  fetchAvailableLayers,
  searchAddresses,
  searchNearbyFeatures,
  getUrlParams,
  calculateBoundingBox,
  calculateDistance,
  calculatePolylineLength,
  calculatePolygonArea,
  calculateBearing,
  metersToFeet,
  metersToMiles,
  sqMetersToSqFeet,
  sqMetersToAcres,
  sqMetersToHectares,
  formatDistance,
  formatArea,
  formatBearing,
  fetchAllCustomAsBuiltFiles,
  isPointWithinRadius,
  customAsBuiltToFeature,
  getCustomAsBuiltFilesAtPoint,
  buildGetFeatureInfoUrl,
  generateViewerUrl,
  getDrawingIdInfo,
  fetchTicketGeoJSON,
  getWorkspaceFromRegion,
  isWMSRenderingBroken,
  fetchLayerFeaturesViaWFS,
} from "./wms-utils";
const defaultZoomLevel = 21;

// Rough Ontario-wide bounds (adjust if needed)
const ONTARIO_BOUNDS = L.latLngBounds(
  [41.5, -95], // SW ~ near SW Ontario / border
  [57, -74] // NE ~ far north / east Ontario
);

// Fix Leaflet default marker icons - use Google Maps style red marker
const defaultIcon = L.divIcon({
  className: "custom-marker-icon",
  html: `<svg width="22" height="40" viewBox="0 0 22 40" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
    <path d="M11 0C4.9 0 0 4.9 0 11c0 7.5 11 29 11 29s11-21.5 11-29C22 4.9 17.1 0 11 0z" fill="#ea4335" stroke="white" stroke-width="1.5"/>
    <circle cx="11" cy="11" r="4.5" fill="white"/>
  </svg>`,
  iconSize: [22, 40],
  iconAnchor: [11, 40],
  popupAnchor: [0, -40],
});
L.Marker.prototype.options.icon = defaultIcon;

// --- Drawing controls hooked to the raw Leaflet map instance ---
function DrawControl({ setIsDrawing, measurementUnit }) {
  const map = useMap();
  const drawnItemsRef = useRef(null);
  const prevUnitRef = useRef(measurementUnit);

  // Update measurement labels when unit changes
  useEffect(() => {
    if (prevUnitRef.current !== measurementUnit && drawnItemsRef.current) {
      // Re-render all labels with new unit
      const items = drawnItemsRef.current;
      items.eachLayer((layer) => {
        // Skip if it's a measurement label marker
        if (layer.options?.icon?.options?.className?.includes("measurement")) {
          return;
        }

        // Remove existing measurement labels for this shape
        items.eachLayer((l) => {
          if (l.options?.icon?.options?.className?.includes("measurement")) {
            if (l._drawnBy === layer._leaflet_id) {
              items.removeLayer(l);
            }
          }
        });

        // Re-add measurement labels with new unit
        if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
          // Polyline - re-add segment labels
          const latlngs = layer.getLatLngs();
          for (let i = 0; i < latlngs.length - 1; i++) {
            const midpoint = L.latLng(
              (latlngs[i].lat + latlngs[i + 1].lat) / 2,
              (latlngs[i].lng + latlngs[i + 1].lng) / 2
            );
            const distance = calculateDistance(latlngs[i], latlngs[i + 1]);

            const label = L.marker(midpoint, {
              icon: L.divIcon({
                className: "measurement-label-marker",
                html: `<div class="measurement-text">${formatDistance(
                  distance,
                  measurementUnit
                )}</div>`,
                iconSize: [0, 0],
              }),
            });
            label._drawnBy = layer._leaflet_id;
            items.addLayer(label);
          }
        } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
          // Polygon/Rectangle - re-add segment and area labels
          let latlngs;

          if (layer instanceof L.Rectangle) {
            const bounds = layer.getBounds();
            latlngs = [
              bounds.getSouthWest(),
              bounds.getSouthEast(),
              bounds.getNorthEast(),
              bounds.getNorthWest(),
            ];
          } else {
            const coords = layer.getLatLngs();
            latlngs = Array.isArray(coords[0]) ? coords[0] : coords;
          }

          // Add segment labels
          for (let i = 0; i < latlngs.length; i++) {
            const next = (i + 1) % latlngs.length;
            const midpoint = L.latLng(
              (latlngs[i].lat + latlngs[next].lat) / 2,
              (latlngs[i].lng + latlngs[next].lng) / 2
            );
            const distance = calculateDistance(latlngs[i], latlngs[next]);

            const label = L.marker(midpoint, {
              icon: L.divIcon({
                className: "measurement-label-marker",
                html: `<div class="measurement-text">${formatDistance(
                  distance,
                  measurementUnit
                )}</div>`,
                iconSize: [0, 0],
              }),
            });
            label._drawnBy = layer._leaflet_id;
            items.addLayer(label);
          }

          // Add area label
          const area = calculatePolygonArea(latlngs);
          const bounds = layer.getBounds();
          const center = bounds.getCenter();

          const areaLabel = L.marker(center, {
            icon: L.divIcon({
              className: "measurement-area-label-marker",
              html: `<div class="measurement-area-text">${formatArea(
                area,
                measurementUnit
              )}</div>`,
              iconSize: [0, 0],
            }),
          });
          areaLabel._drawnBy = layer._leaflet_id;
          items.addLayer(areaLabel);
        }
      });

      prevUnitRef.current = measurementUnit;
    }
  }, [measurementUnit, map]);

  useEffect(() => {
    // Create a custom pane for drawn items with high z-index to render above all other layers
    const paneName = "drawnItemsPane";
    let pane = map.getPane(paneName);
    if (!pane) {
      pane = map.createPane(paneName);
      pane.style.zIndex = 700; // Higher than AsBuilt circles (650) and WMS layers (200+)
      pane.style.pointerEvents = "auto";
    }

    // Use SVG renderer for drawn items to ensure proper z-index control
    const svgRenderer = L.svg({ pane: paneName });

    const drawnItems = new L.FeatureGroup();
    drawnItemsRef.current = drawnItems;
    map.addLayer(drawnItems);

    const DrawControl = L.Control.Draw;
    const drawControl = new DrawControl({
      position: "topleft",
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: {
            color: "#3388ff",
            weight: 3,
            opacity: 0.8,
            fillColor: "#3388ff",
            fillOpacity: 0.2,
          },
        },
        polyline: {
          shapeOptions: {
            color: "#3388ff",
            weight: 3,
            opacity: 0.8,
          },
        },
        rectangle: {
          shapeOptions: {
            color: "#3388ff",
            weight: 3,
            opacity: 0.8,
            fillColor: "#3388ff",
            fillOpacity: 0.2,
          },
        },
        marker: true,
        circle: false,
        circlemarker: false,
      },
      edit: { featureGroup: drawnItems, remove: true },
    });

    map.addControl(drawControl);

    const drawEvents = L.Draw.Event;

    // Add measurement labels on segments
    function addMeasurementLabels(layer) {
      if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
        // Polyline - add distance labels on each segment
        const latlngs = layer.getLatLngs();
        const distance = calculatePolylineLength(latlngs);

        for (let i = 0; i < latlngs.length - 1; i++) {
          const midpoint = L.latLng(
            (latlngs[i].lat + latlngs[i + 1].lat) / 2,
            (latlngs[i].lng + latlngs[i + 1].lng) / 2
          );
          const segmentDistance = calculateDistance(latlngs[i], latlngs[i + 1]);

          const label = L.marker(midpoint, {
            icon: L.divIcon({
              className: "measurement-label-marker",
              html: `<div class="measurement-text">${formatDistance(
                segmentDistance,
                measurementUnit
              )}</div>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0],
            }),
          });
          label._drawnBy = layer._leaflet_id;
          label.addTo(map);
          drawnItems.addLayer(label);
        }
      } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
        // Polygon/Rectangle - add distance labels on each side + area in center
        let latlngs;

        if (layer instanceof L.Rectangle) {
          const bounds = layer.getBounds();
          latlngs = [
            bounds.getSouthWest(),
            bounds.getSouthEast(),
            bounds.getNorthEast(),
            bounds.getNorthWest(),
          ];
        } else {
          latlngs = layer.getLatLngs()[0];
        }

        const area = calculatePolygonArea(latlngs);

        // Add distance labels on each edge
        for (let i = 0; i < latlngs.length; i++) {
          const next = (i + 1) % latlngs.length;
          const midpoint = L.latLng(
            (latlngs[i].lat + latlngs[next].lat) / 2,
            (latlngs[i].lng + latlngs[next].lng) / 2
          );
          const segmentDistance = calculateDistance(latlngs[i], latlngs[next]);

          const label = L.marker(midpoint, {
            icon: L.divIcon({
              className: "measurement-label-marker",
              html: `<div class="measurement-text">${formatDistance(
                segmentDistance,
                measurementUnit
              )}</div>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0],
            }),
          });
          label._drawnBy = layer._leaflet_id;
          label.addTo(map);
          drawnItems.addLayer(label);
        }

        // Add area label in the center
        const bounds = layer.getBounds();
        const center = bounds.getCenter();

        const areaLabel = L.marker(center, {
          icon: L.divIcon({
            className: "measurement-area-label-marker",
            html: `<div class="measurement-area-text">${formatArea(
              area,
              measurementUnit
            )}</div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
          }),
        });
        areaLabel._drawnBy = layer._leaflet_id;
        areaLabel.addTo(map);
        drawnItems.addLayer(areaLabel);
      } else if (layer instanceof L.Marker) {
        // Marker - show coordinates as tooltip
        const latlng = layer.getLatLng();
        layer.bindTooltip(
          `Lat: ${latlng.lat.toFixed(6)}<br/>Lng: ${latlng.lng.toFixed(6)}`,
          { permanent: false, direction: "top" }
        );
      }
    }

    function onCreated(e) {
      // Remove the original layer (created with Canvas renderer)
      map.removeLayer(e.layer);

      // Recreate with SVG renderer to ensure visibility
      let newLayer;
      if (e.layer instanceof L.Polyline && !(e.layer instanceof L.Polygon)) {
        newLayer = L.polyline(e.layer.getLatLngs(), {
          color: "#3388ff",
          weight: 3,
          opacity: 0.8,
          renderer: svgRenderer,
        });
      } else if (e.layer instanceof L.Polygon) {
        newLayer = L.polygon(e.layer.getLatLngs(), {
          color: "#3388ff",
          weight: 3,
          opacity: 0.8,
          fillColor: "#3388ff",
          fillOpacity: 0.2,
          renderer: svgRenderer,
        });
      } else if (e.layer instanceof L.Rectangle) {
        newLayer = L.rectangle(e.layer.getBounds(), {
          color: "#3388ff",
          weight: 3,
          opacity: 0.8,
          fillColor: "#3388ff",
          fillOpacity: 0.2,
          renderer: svgRenderer,
        });
      } else {
        // For markers, use original layer
        newLayer = e.layer;
      }

      drawnItems.addLayer(newLayer);
      addMeasurementLabels(newLayer);
    }
    function onEdited(e) {
      // Remove all measurement labels and recreate them
      const labelsToRemove = [];
      drawnItems.eachLayer((l) => {
        if (
          l instanceof L.Marker &&
          (l.options.icon?.options?.className === "measurement-label-marker" ||
            l.options.icon?.options?.className ===
              "measurement-area-label-marker")
        ) {
          labelsToRemove.push(l);
        }
      });
      labelsToRemove.forEach((l) => drawnItems.removeLayer(l));

      // Re-add measurement labels and ensure styling/renderer for all shapes
      if (e.layers) {
        e.layers.eachLayer((layer) => {
          // Reapply styling and renderer to ensure visibility after edit
          if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
            layer.setStyle({
              color: "#3388ff",
              weight: 3,
              opacity: 0.8,
            });
            if (layer.setRenderer) {
              layer.setRenderer(svgRenderer);
            }
          } else if (
            layer instanceof L.Polygon ||
            layer instanceof L.Rectangle
          ) {
            layer.setStyle({
              color: "#3388ff",
              weight: 3,
              opacity: 0.8,
              fillColor: "#3388ff",
              fillOpacity: 0.2,
            });
            if (layer.setRenderer) {
              layer.setRenderer(svgRenderer);
            }
          }
          addMeasurementLabels(layer);
        });
      }
    }
    function onDeleted(e) {
      // No action needed when drawings are deleted
    }

    map.on(drawEvents.CREATED, onCreated);
    map.on(drawEvents.EDITED, onEdited);
    map.on(drawEvents.DELETED, onDeleted);

    // Track drawing state to disable feature info
    map.on("draw:drawstart", () => setIsDrawing(true));
    map.on("draw:drawstop", () => setIsDrawing(false));
    map.on("draw:editstart", () => setIsDrawing(true));
    map.on("draw:editstop", () => setIsDrawing(false));
    map.on("draw:deletestart", () => setIsDrawing(true));
    map.on("draw:deletestop", () => setIsDrawing(false));

    return () => {
      map.off(drawEvents.CREATED, onCreated);
      map.off(drawEvents.EDITED, onEdited);
      map.off(drawEvents.DELETED, onDeleted);
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
    };
  }, [map]);

  return null;
}

// Component to render custom AsBuilt file circles on the map
function CustomAsBuiltCircles({ customAsBuiltFiles, visible }) {
  const map = useMap();

  useEffect(() => {
    if (!visible || !customAsBuiltFiles || customAsBuiltFiles.length === 0) {
      return;
    }

    // Create SVG renderer for circles (map uses Canvas, but we need SVG for proper z-index control)
    const svgRenderer = L.svg({ pane: "customAsBuiltPane" });

    // Create a custom pane for circles with high z-index to render above WMS layers
    const paneName = "customAsBuiltPane";
    let pane = map.getPane(paneName);
    if (!pane) {
      pane = map.createPane(paneName);
      pane.style.zIndex = 650;
      pane.style.pointerEvents = "auto";
    }

    // Simple safety limit – avoid flooding map with thousands of circles
    const MAX_CIRCLES = 500;
    const filesToRender = customAsBuiltFiles.slice(0, MAX_CIRCLES);

    // Limit rendering to avoid performance issues

    const circles = [];

    filesToRender.forEach((file) => {
      const radius = file.radius || 100;

      const circle = L.circle([file.lat, file.lng], {
        renderer: svgRenderer,
        radius: radius,
        color: "#ec4899",
        fillColor: "transparent",
        fillOpacity: 0,
        weight: 3,
        opacity: 1,
        dashArray: "5, 5",
        interactive: true,
      }).addTo(map);

      circles.push(circle);
    });

    return () => {
      circles.forEach((circle) => {
        map.removeLayer(circle);
      });
    };
  }, [map, customAsBuiltFiles, visible]);

  return null;
}

// Component to render ticket GeoJSON on the top layer
function TicketGeoJSONLayer({ ticketGeoJSON }) {
  const map = useMap();

  useEffect(() => {
    if (!ticketGeoJSON) {
      return;
    }

    // Create a custom pane for ticket GeoJSON with highest z-index
    const paneName = "ticketGeoJSONPane";
    let pane = map.getPane(paneName);
    if (!pane) {
      pane = map.createPane(paneName);
      pane.style.zIndex = 800; // Higher than drawn items (700) and AsBuilt circles (650)
      pane.style.pointerEvents = "auto";
    }

    // Use SVG renderer for ticket GeoJSON
    const svgRenderer = L.svg({ pane: paneName });

    // Style for ticket GeoJSON - make it prominent
    const style = {
      // weight: 1,
      // opacity: 1,
      // fillOpacity: 0.2,
    };

    // Create GeoJSON layer
    const geoJSONLayer = L.geoJSON(ticketGeoJSON, {
      style: style,
      renderer: svgRenderer,
      onEachFeature: (feature, layer) => {
        if (feature.properties) {
          const props = feature.properties;
          const popupContent = Object.entries(props)
            .map(([key, value]) => `<b>${key}:</b> ${value}`)
            .join("<br>");
          layer.bindPopup(popupContent);
        }
      },
    }).addTo(map);

    return () => {
      map.removeLayer(geoJSONLayer);
    };
  }, [map, ticketGeoJSON]);

  return null;
}

// Renders a layer client-side from WFS data instead of WMSTileLayer, for
// layers whose WMS GetMap/GetFeatureInfo is broken server-side (see
// isWMSRenderingBroken in wms-utils.js)
function WFSFallbackLayer({ features, styleConfig }) {
  const map = useMap();

  useEffect(() => {
    if (!features || features.length === 0) {
      return;
    }

    const paneName = "wfsFallbackPane";
    let pane = map.getPane(paneName);
    if (!pane) {
      pane = map.createPane(paneName);
      pane.style.zIndex = 250; // alongside WMS overlays
    }

    const layer = L.geoJSON(
      { type: "FeatureCollection", features },
      {
        pane: paneName,
        pointToLayer: (_feature, latlng) =>
          L.circleMarker(latlng, {
            radius: 6,
            color: "#ffffff",
            weight: 1.5,
            fillColor: styleConfig.color,
            fillOpacity: styleConfig.opacity,
            opacity: 1,
          }),
        onEachFeature: (feature, featureLayer) => {
          const props = feature.properties || {};
          const popupContent = Object.entries(props)
            .filter(([k]) => !k.startsWith("_"))
            .map(([k, v]) => `<b>${k}:</b> ${v}`)
            .join("<br>");
          featureLayer.bindPopup(popupContent || "No properties available");
        },
      }
    ).addTo(map);

    return () => {
      map.removeLayer(layer);
    };
  }, [map, features, styleConfig]);

  return null;
}

// Handle map clicks: send GetFeatureInfo and show results in a popup
function MapClickInfo({
  visibleLayerKeys,
  setPopup,
  spatialFilter,
  isDrawing,
  customAsBuiltFiles,
  showCustomAsBuiltLayer,
  region,
}) {
  const map = useMap();

  useMapEvents({
    async click(e) {
      // Don't query features when drawing
      if (isDrawing) {
        return;
      }

      let wmsFeatures = [];
      let content = "";

      // Query WMS layers if any are visible (region-aware)
      if (visibleLayerKeys.length > 0) {
        const url = buildGetFeatureInfoUrl(
          map,
          e.latlng,
          visibleLayerKeys,
          region
        );

        if (url) {
          try {
            const resp = await fetch(url);
            const ct = resp.headers.get("content-type") || "";

            if (ct.includes("application/json")) {
              const json = await resp.json();
              wmsFeatures = json.features || [];
            } else {
              // Fallback: many GeoServer setups return HTML/text
              const text = await resp.text();
              content = text || "<b>No info returned.</b>";
            }
          } catch (err) {
            const errorMessage =
              err instanceof Error ? err.message : String(err);
            content = `<b>WMS Error</b><div>${errorMessage}</div>`;
          }
        }
      }

      // Check for custom AsBuilt files at clicked location (if layer is enabled)
      let customAsBuiltFeatures = [];
      if (showCustomAsBuiltLayer) {
        customAsBuiltFeatures = getCustomAsBuiltFilesAtPoint(
          e.latlng.lat,
          e.latlng.lng,
          customAsBuiltFiles
        );
      }

      // Merge WMS features with custom AsBuilt files
      const allFeatures = [...wmsFeatures, ...customAsBuiltFeatures];

      // Determine what to show
      if (allFeatures.length === 0) {
        // No features found at all
        if (visibleLayerKeys.length === 0 && !showCustomAsBuiltLayer) {
          content = "No layers selected.";
        } else {
          content = ""; // Will show "No features found" in popup
        }
        setPopup({
          latlng: e.latlng,
          features: [],
          content,
        });
      } else {
        // Clear content if we have features (especially custom AsBuilt files)
        setPopup({ latlng: e.latlng, features: allFeatures, content: "" });
      }
    },
  });

  return null;
}

// Component to handle URL-based center and spatial filtering
function UrlBasedMapController({ urlParams, spatialFilter, zoomLevel }) {
  const map = useMap();

  useEffect(() => {
    if (urlParams.lat && urlParams.lng) {
      // Center map on URL coordinates with specified zoom level
      map.setView([urlParams.lat, urlParams.lng], defaultZoomLevel);

      // Add a marker for the target location
      const marker = L.marker([urlParams.lat, urlParams.lng])
        .addTo(map)
        .bindPopup(
          `Target Location<br/>Lat: ${urlParams.lat}<br/>Lng: ${urlParams.lng}`
        );

      // Show the spatial filter area if provided
      if (spatialFilter) {
        const rectangle = L.rectangle(spatialFilter, {
          color: "#ff7800",
          weight: 2,
          fillOpacity: 0.1,
        }).addTo(map);

        rectangle.bindPopup(`Search Area<br/>Radius: ${urlParams.radius}m`);
      }

      return () => {
        map.removeLayer(marker);
        if (spatialFilter) {
          map.eachLayer((layer) => {
            if (layer instanceof L.Rectangle) {
              map.removeLayer(layer);
            }
          });
        }
      };
    }
  }, [map, urlParams, spatialFilter, defaultZoomLevel]);

  return null;
}

// Component to handle search result highlighting
function SearchResultController({ selectedResult, nearbyFeatures }) {
  const map = useMap();

  useEffect(() => {
    if (selectedResult) {
      // Zoom to the selected location
      map.setView(selectedResult.coordinates, defaultZoomLevel);

      // Create a highlight marker with custom styling
      const highlightIcon = L.divIcon({
        className: "search-highlight-marker",
        html: `
          <div style="
            background: #ff4444; 
            border: 3px solid white; 
            border-radius: 50%; 
            width: 20px; 
            height: 20px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            animation: pulse 2s infinite;
          "></div>
          <style>
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.2); opacity: 0.7; }
              100% { transform: scale(1); opacity: 1; }
            }
          </style>
        `,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      const marker = L.marker(selectedResult.coordinates, {
        icon: highlightIcon,
      })
        .addTo(map)
        .bindPopup(
          `
          <div>
            <strong>📍 ${selectedResult.address}</strong>
            <br/>
            <small>Lat: ${selectedResult.coordinates[0].toFixed(6)}</small>
            <br/>
            <small>Lng: ${selectedResult.coordinates[1].toFixed(6)}</small>
          </div>
        `
        )
        .openPopup();

      // Add a search radius circle
      const circle = L.circle(selectedResult.coordinates, {
        radius: 20, // 20 meters (reduced from 50)
        color: "#ff4444",
        fillColor: "#ff4444",
        fillOpacity: 0.15,
        weight: 2,
        dashArray: "5, 5",
      }).addTo(map);

      return () => {
        map.removeLayer(marker);
        map.removeLayer(circle);
      };
    }
  }, [map, selectedResult, nearbyFeatures]);

  return null;
}

export default function LocatorMap() {
  const urlParams = useMemo(() => getUrlParams(), []);
  const region = urlParams.region || null; // Get region from URL params

  // Get region-specific layers
  const LAYERS = useMemo(() => getLayersForRegion(region), [region]);
  const DEFAULT_VISIBLE_LAYER_KEYS = useMemo(
    () => getDefaultVisibleLayerKeys(region),
    [region]
  );
  const workspace = useMemo(() => getWorkspaceFromRegion(region), [region]);

  const [visible, setVisible] = useState(
    () => new Set(DEFAULT_VISIBLE_LAYER_KEYS)
  ); // default: core set of layers only
  const [popup, setPopup] = useState(null);
  const [availableLayers, setAvailableLayers] = useState([]);
  const [showAvailableLayers, setShowAvailableLayers] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);

  // Update visible layers when region changes
  useEffect(() => {
    setVisible(new Set(DEFAULT_VISIBLE_LAYER_KEYS));
  }, [region, DEFAULT_VISIBLE_LAYER_KEYS]);

  // Custom GeoJSON layers
  const [customLayers, setCustomLayers] = useState([]);

  // Measurement unit state
  const [measurementUnit, setMeasurementUnit] = useState("metric");

  // Basemap selection state
  const [basemap, setBasemap] = useState("osm"); // 'osm', 'google-standard', 'google-satellite', 'google-satellite-no-labels'

  // Search functionality state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [nearbyFeatures, setNearbyFeatures] = useState([]);

  // Custom AsBuilt files state
  const [customAsBuiltFiles, setCustomAsBuiltFiles] = useState([]);
  const [showCustomAsBuiltLayer, setShowCustomAsBuiltLayer] = useState(true); // Default: show

  // Ticket GeoJSON state
  const [ticketGeoJSON, setTicketGeoJSON] = useState(null);
  const [showTicketGeoJSON, setShowTicketGeoJSON] = useState(true); // Default: show

  // Data for layers whose WMS rendering is broken server-side; fetched via
  // WFS instead and rendered client-side (see isWMSRenderingBroken)
  const [wfsFallbackData, setWfsFallbackData] = useState({});

  const spatialFilter = useMemo(() => {
    if (urlParams.lat && urlParams.lng && urlParams.radius) {
      return calculateBoundingBox(
        urlParams.lat,
        urlParams.lng,
        urlParams.radius
      );
    }
    return undefined;
  }, [urlParams]);

  const visibleLayerKeys = useMemo(() => Array.from(visible), [visible]);

  // Layers actually queryable via WMS GetFeatureInfo (excludes layers rendered
  // via the WFS fallback, since their WMS GetFeatureInfo is confirmed broken)
  const wmsQueryableLayerKeys = useMemo(
    () => visibleLayerKeys.filter((key) => !isWMSRenderingBroken(key)),
    [visibleLayerKeys]
  );

  // Default center - use URL params if available, otherwise Waterloo, Ontario
  const defaultCenter =
    urlParams.lat && urlParams.lng
      ? [urlParams.lat, urlParams.lng]
      : [43.4643, -80.5204]; // Waterloo, Ontario coordinates

  const defaultZoom = urlParams.lat && urlParams.lng ? defaultZoomLevel : 19;

  function toggleLayer(key) {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function selectAllLayers() {
    setVisible(new Set(LAYERS.map((l) => l.key)));
  }

  function deselectAllLayers() {
    setVisible(new Set());
  }

  // Handle GeoJSON file upload
  function handleGeoJSONUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const geojson = JSON.parse(e.target?.result);
        const newLayer = {
          id: `custom-${Date.now()}`,
          name: file.name.replace(".geojson", "").replace(".json", ""),
          data: geojson,
          visible: true,
          color: "#ff00ff", // Magenta for Ontario One Call
        };
        setCustomLayers((prev) => [...prev, newLayer]);
      } catch (error) {
        alert("Error loading GeoJSON file. Please check the format.");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }

  function toggleCustomLayer(id) {
    setCustomLayers((prev) =>
      prev.map((layer) =>
        layer.id === id ? { ...layer, visible: !layer.visible } : layer
      )
    );
  }

  function removeCustomLayer(id) {
    setCustomLayers((prev) => prev.filter((layer) => layer.id !== id));
  }

  // Fetch available layers on component mount (region-aware)
  useEffect(() => {
    fetchAvailableLayers(region).then(setAvailableLayers);
  }, [region]);

  // Fetch custom AsBuilt files on component mount
  useEffect(() => {
    async function loadCustomAsBuiltFiles() {
      const files = await fetchAllCustomAsBuiltFiles();
      setCustomAsBuiltFiles(files);
    }
    loadCustomAsBuiltFiles();
  }, []);

  // Fetch ticket GeoJSON when ticketNo is in URL params
  useEffect(() => {
    async function loadTicketGeoJSON() {
      if (urlParams.ticketNo) {
        const geoJSON = await fetchTicketGeoJSON(urlParams.ticketNo);
        setTicketGeoJSON(geoJSON);
      } else {
        setTicketGeoJSON(null);
      }
    }
    loadTicketGeoJSON();
  }, [urlParams.ticketNo]);

  // Fetch data via WFS for any visible layer whose WMS rendering is broken
  useEffect(() => {
    const brokenKeysToLoad = visibleLayerKeys.filter(
      (key) => isWMSRenderingBroken(key) && !wfsFallbackData[key]
    );
    if (brokenKeysToLoad.length === 0) return;

    brokenKeysToLoad.forEach(async (key) => {
      const features = await fetchLayerFeaturesViaWFS(key, region);
      setWfsFallbackData((prev) => ({ ...prev, [key]: features }));
    });
  }, [visibleLayerKeys, region, wfsFallbackData]);

  // Debounced search functionality (auto-search as user types)
  useEffect(() => {
    const delayTimer = setTimeout(async () => {
      if (searchTerm.trim().length >= 3) {
        // Only search if at least 3 characters
        setIsSearching(true);
        try {
          const results = await searchAddresses(searchTerm);
          setSearchResults(results);
        } catch (error) {
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else if (searchTerm.trim().length === 0) {
        // Clear results when search is empty
        setSearchResults([]);
      }
    }, 500); // 500ms delay after user stops typing

    return () => clearTimeout(delayTimer);
  }, [searchTerm]);

  // Manual search trigger (for Enter key)
  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    try {
      const results = await searchAddresses(searchTerm);
      setSearchResults(results);
    } catch (error) {
      // Silently handle search error
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search result selection
  const handleResultSelect = async (result) => {
    setSelectedResult(result);
    setSearchResults([]); // Hide search results

    // Search for nearby features (region-aware)
    const nearby = await searchNearbyFeatures(
      result.coordinates,
      20, // 20 meter radius (matches circle size)
      visibleLayerKeys,
      region
    );
    setNearbyFeatures(nearby);
  };

  return (
    <div className={styles.container}>
      {/* Sidebar: layer toggles */}
      <div className={styles.sidebar}>
        <h2 className={styles.title}>TCU WMS Viewer</h2>
        {region && (
          <div
            style={{
              marginBottom: "12px",
              padding: "8px 12px",
              background: "#e0f2fe",
              borderRadius: "6px",
              fontSize: "12px",
              color: "#0369a1",
              fontWeight: "600",
            }}
          >
            Region: {region} ({workspace})
          </div>
        )}

        {/* Search Section */}
        <div className={styles.searchSection}>
          <div className={styles.searchHeader}>
            <h4 className={styles.searchTitle}>🔍 Search Address</h4>
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSearchResults([]);
                  setSelectedResult(null);
                }}
                className={styles.clearButton}
              >
                Clear
              </button>
            )}
          </div>
          <div className={styles.searchInputContainer}>
            <input
              type="text"
              placeholder="e.g. University of Waterloo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className={`${styles.searchInput} ${
                isSearching ? styles.searching : ""
              }`}
            />
            {isSearching && (
              <div className={styles.searchingIndicator}>🔄 Searching...</div>
            )}
          </div>

          {/* Helper text */}
          {searchTerm.length > 0 && searchTerm.length < 3 && (
            <div className={styles.searchHelperText}>
              Type {3 - searchTerm.length} more character
              {3 - searchTerm.length > 1 ? "s" : ""}...
            </div>
          )}

          {/* Search Results */}
          {searchTerm.length >= 3 &&
            !isSearching &&
            searchResults.length === 0 &&
            !selectedResult && (
              <div className={styles.searchNoResults}>
                ℹ️ No results found for "{searchTerm}"
                <br />
                <span className={styles.searchNoResultsSubtext}>
                  Try a different location in Waterloo region
                </span>
              </div>
            )}

          {searchResults.length > 0 && (
            <div className={styles.searchResultsContainer}>
              <div className={styles.searchResultsHeader}>
                {searchResults.length} result
                {searchResults.length > 1 ? "s" : ""} found
              </div>
              {searchResults.map((result, idx) => (
                <div
                  key={result.id}
                  onClick={() => handleResultSelect(result)}
                  className={styles.searchResultItem}
                >
                  <span className={styles.searchResultIcon}>📍</span>
                  <div className={styles.searchResultContent}>
                    <div className={styles.searchResultPrimary}>
                      {result.address.split(",")[0]}
                    </div>
                    {result.address.includes(",") && (
                      <div className={styles.searchResultSecondary}>
                        {result.address.split(",").slice(1).join(",")}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Selected Result Info */}
          {selectedResult && (
            <div className={styles.selectedResultInfo}>
              <strong className={styles.selectedResultLabel}>
                📍 Selected:
              </strong>{" "}
              <span className={styles.selectedResultValue}>
                {selectedResult.address}
              </span>
              <br />
              <button
                onClick={() => {
                  setSelectedResult(null);
                  setNearbyFeatures([]);
                }}
                className={styles.selectedResultClearButton}
              >
                Clear
              </button>
            </div>
          )}

          {/* Nearby Features */}
          {nearbyFeatures.length > 0 && (
            <div className={styles.nearbyFeatures}>
              <strong className={styles.nearbyFeaturesTitle}>
                🏗️ Nearby Infrastructure:
              </strong>
              {nearbyFeatures.map((layer) => (
                <div key={layer.layerKey} className={styles.nearbyFeatureItem}>
                  <strong>{layer.layerKey.split(":")[1]}:</strong>{" "}
                  {layer.features.length} features
                </div>
              ))}
            </div>
          )}
        </div>

        {/* URL Parameters Info */}
        {urlParams.lat && urlParams.lng && (
          <div className={styles.urlParamsInfo}>
            <strong className={styles.urlParamsTitle}>
              🎯 Target Location:
            </strong>
            <div className={styles.urlParamsContent}>
              <div>Lat: {urlParams.lat.toFixed(6)}</div>
              <div>Lng: {urlParams.lng.toFixed(6)}</div>
              <div>Radius: {urlParams.radius}m</div>
            </div>
          </div>
        )}

        {/* Basemap Selector */}
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>🗺️ Basemap</h4>
          <select
            value={basemap}
            onChange={(e) => setBasemap(e.target.value)}
            className={styles.select}
          >
            <option value="osm">OpenStreetMap</option>
            <option value="google-standard">Google Standard Map</option>
            <option value="google-satellite">Google Satellite Map</option>
            <option value="google-satellite-no-labels">
              Google Satellite (No Labels)
            </option>
          </select>
          <div className={styles.selectHelperText}>
            {basemap === "osm" && "• OpenStreetMap standard tiles"}
            {basemap === "google-standard" && "• Google Maps standard view"}
            {basemap === "google-satellite" &&
              "• Google Satellite imagery with labels"}
            {basemap === "google-satellite-no-labels" &&
              "• Google Satellite imagery without labels"}
          </div>
        </div>

        {/* Measurement Unit Selector */}
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>📏 Measurement Units</h4>
          <select
            value={measurementUnit}
            onChange={(e) => setMeasurementUnit(e.target.value)}
            className={styles.select}
          >
            <option value="metric">Metric (m, km, m², ha)</option>
            <option value="imperial">Imperial (ft, mi, ft², acres)</option>
            <option value="mixed">Mixed (m + ft)</option>
          </select>
          <div className={styles.selectHelperText}>
            {measurementUnit === "metric" && "• Distances in meters/kilometers"}
            {measurementUnit === "imperial" && "• Distances in feet/miles"}
            {measurementUnit === "mixed" && "• Shows both metric and imperial"}
          </div>
        </div>

        <h4 className={styles.sectionHeader}>📚 Layers</h4>

        {/* Select All / Deselect All buttons */}
        <div className={styles.buttonGroup}>
          <button
            onClick={selectAllLayers}
            className={`${styles.button} ${styles.buttonSelectAll}`}
          >
            ✓ Select All
          </button>
          <button
            onClick={deselectAllLayers}
            className={`${styles.button} ${styles.buttonDeselectAll}`}
          >
            ✕ Deselect All
          </button>
        </div>

        {/* Button to show/hide available layers */}
        <button
          onClick={() => setShowAvailableLayers(!showAvailableLayers)}
          className={styles.buttonToggle}
        >
          {showAvailableLayers ? "▼" : "▶"}{" "}
          {showAvailableLayers ? "Hide" : "Show"} Available Layers (
          {availableLayers.length})
        </button>

        {/* Show available layers from GetCapabilities */}
        {showAvailableLayers && (
          <div className={styles.availableLayers}>
            <strong className={styles.availableLayersTitle}>
              Available layers from GetCapabilities:
            </strong>
            {availableLayers.length > 0 ? (
              <ul className={styles.availableLayersList}>
                {availableLayers.map((layer) => (
                  <li key={layer} className={styles.availableLayersItem}>
                    <code className={styles.availableLayersCode}>{layer}</code>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.availableLayersLoading}>Loading...</p>
            )}
          </div>
        )}

        {/* Render layers grouped by category */}
        {[
          {
            title: "🏢 General Infrastructure",
            layers: LAYERS.filter(
              (l) =>
                l.key.includes("Roads") ||
                l.key.includes("Addresses") ||
                l.key.includes("Boundary") ||
                l.key.includes("City") ||
                l.key.includes("Civic_Address") ||
                l.key.includes("Parcel_")
            ),
          },
          {
            title: "🌧️ Storm Water",
            layers: LAYERS.filter(
              (l) =>
                l.key.includes("RSW_") ||
                l.key.includes("SS_") ||
                l.key.includes("SS_CATCHBASIN") ||
                l.key.includes("SS_INFILTRATION") ||
                l.key.includes("SS_MANHOLE") ||
                l.key.includes("SS_ML_LEAD") ||
                l.key.includes("SS_OUTFALL")
            ),
          },
          {
            title: "💧 Water Network",
            layers: LAYERS.filter(
              (l) => l.key.includes("RWN_") || l.key.includes("Water_")
            ),
          },
          {
            title: "🚰 Wastewater",
            layers: LAYERS.filter((l) => l.key.includes("RWWN_")),
          },
        ]
          .filter((group) => group.layers.length > 0) // Only show groups that have layers
          .map((group) => (
            <div key={group.title} className={styles.layerGroup}>
              <h5 className={styles.layerGroupTitle}>{group.title}</h5>
              {group.layers.map((l) => {
                const styleConfig =
                  LAYER_STYLES[l.style] || LAYER_STYLES.general;

                return (
                  <label
                    key={l.key}
                    className={`${styles.layerItem} ${
                      visible.has(l.key)
                        ? styles.layerItemActive
                        : styles.layerItemInactive
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={visible.has(l.key)}
                      onChange={() => toggleLayer(l.key)}
                      className={styles.layerCheckbox}
                    />
                    {/* Color indicator */}
                    <div
                      className={styles.colorIndicator}
                      style={{
                        backgroundColor: styleConfig.color,
                      }}
                      title={`Color: ${styleConfig.color}, Opacity: ${styleConfig.opacity}`}
                    />
                    <span className={styles.layerTitle}>{l.title}</span>
                    {visible.has(l.key) && (
                      <span className={styles.layerCheckmark}>✓</span>
                    )}
                  </label>
                );
              })}
            </div>
          ))}

        {/* Ticket GeoJSON Layer */}
        {ticketGeoJSON && (
          <div className={styles.customAsBuiltSection}>
            <h4 className={styles.sectionHeader}>🎫 Ticket GeoJSON</h4>
            <label
              className={`${styles.customAsBuiltItem} ${
                showTicketGeoJSON
                  ? styles.customAsBuiltItemActive
                  : styles.customAsBuiltItemInactive
              }`}
            >
              <input
                type="checkbox"
                checked={showTicketGeoJSON}
                onChange={() => setShowTicketGeoJSON(!showTicketGeoJSON)}
                className={styles.layerCheckbox}
              />
              {/* Color indicator - blue/green */}
              <div
                className={styles.customAsBuiltColorIndicator}
                style={{
                  backgroundColor: "#dbeafe",
                  borderColor: "#3b82f6",
                }}
              />
              <span className={styles.layerTitle}>
                GeoJSON Layer
                {urlParams.ticketNo && (
                  <span style={{ fontSize: "10px", color: "#64748b" }}>
                    {" "}
                    ({urlParams.ticketNo})
                  </span>
                )}
              </span>
              {showTicketGeoJSON && (
                <span className={styles.customAsBuiltCheckmark}>✓</span>
              )}
            </label>
          </div>
        )}

        {/* Custom AsBuilt Files Layer */}
        <div className={styles.customAsBuiltSection}>
          <h4 className={styles.sectionHeader}>📄 Custom AsBuilt Files</h4>
          <label
            className={`${styles.customAsBuiltItem} ${
              showCustomAsBuiltLayer
                ? styles.customAsBuiltItemActive
                : styles.customAsBuiltItemInactive
            }`}
          >
            <input
              type="checkbox"
              checked={showCustomAsBuiltLayer}
              onChange={() =>
                setShowCustomAsBuiltLayer(!showCustomAsBuiltLayer)
              }
              className={styles.layerCheckbox}
            />
            {/* Color indicator - light pink */}
            <div
              className={styles.customAsBuiltColorIndicator}
              title="Light pink circles showing custom AsBuilt file locations"
            />
            <span className={styles.layerTitle}>
              Custom AsBuilt Files ({customAsBuiltFiles.length})
            </span>
            {showCustomAsBuiltLayer && (
              <span className={styles.customAsBuiltCheckmark}>✓</span>
            )}
          </label>
          {customAsBuiltFiles.length > 0 && (
            <div className={styles.customAsBuiltHelperText}>
              • Light pink circles show file locations
              <br />• Click within circle to view file
            </div>
          )}
        </div>

        {/* Custom GeoJSON Layers Section */}
        <div className={styles.customLayersSection}>
          <h4 className={styles.sectionHeader}>📁 Custom Layers (GeoJSON)</h4>

          {/* Upload button */}
          <label className={styles.uploadButton}>
            📤 Upload GeoJSON
            <input
              type="file"
              accept=".geojson,.json"
              onChange={handleGeoJSONUpload}
            />
          </label>

          {/* List of custom layers */}
          {customLayers.length > 0 ? (
            <div>
              {customLayers.map((layer) => (
                <div
                  key={layer.id}
                  className={`${styles.customLayerItem} ${
                    layer.visible
                      ? styles.customLayerItemVisible
                      : styles.customLayerItemHidden
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={layer.visible}
                    onChange={() => toggleCustomLayer(layer.id)}
                    className={styles.layerCheckbox}
                  />
                  <div
                    className={styles.customLayerColorIndicator}
                    style={{
                      backgroundColor: layer.color,
                    }}
                  />
                  <span className={styles.customLayerName}>{layer.name}</span>
                  <button
                    onClick={() => removeCustomLayer(layer.id)}
                    className={styles.removeLayerButton}
                    title="Remove layer"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noCustomLayers}>No custom layers loaded</div>
          )}
        </div>

        {/* Layer Type Legend */}
        <div className={styles.colorLegend}>
          <strong className={styles.colorLegendTitle}>🎨 Layer Types</strong>
          <div className={styles.colorLegendList}>
            <div className={styles.colorLegendItem}>
              <div
                className={styles.colorLegendDot}
                style={{
                  width: "20px",
                  height: "3px",
                  borderRadius: "0",
                  // backgroundColor: "#2563eb",
                }}
              />
              <span className={styles.colorLegendText}>
                <strong>Mains</strong> - Bold Lines
              </span>
            </div>
            <div className={styles.colorLegendItem}>
              <div
                className={styles.colorLegendDot}
                style={{
                  width: "20px",
                  height: "2px",
                  borderRadius: "0",
                  // background: "repeating-linear-gradient(to right, #2563eb 0px, #2563eb 3px, transparent 3px, transparent 6px)",
                }}
              />
              <span className={styles.colorLegendText}>
                <strong>Leads & Services</strong> - Dotted Lines
              </span>
            </div>
            <div className={styles.colorLegendItem}>
              <div
                className={styles.colorLegendDot}
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  // backgroundColor: "#2563eb",
                }}
              />
              <span className={styles.colorLegendText}>
                <strong>Chambers & Manholes</strong> - Circles
              </span>
            </div>
            <div className={styles.colorLegendItem}>
              <div
                className={styles.colorLegendDot}
                style={{
                  width: "20px",
                  height: "2px",
                  borderRadius: "0",
                  // backgroundColor: "#64748b",
                  opacity: 0.4,
                }}
              />
              <span className={styles.colorLegendText}>
                <strong>Roads</strong> - Faded
              </span>
            </div>
          </div>
          <div
            style={{
              marginTop: "8px",
              fontSize: "10px",
              color: "#64748b",
              fontStyle: "italic",
            }}
          >
            * Colors and styles are rendered by the server
          </div>
        </div>

        {/* Debug info */}
        <div className={styles.debugInfo}>
          <strong className={styles.debugInfoTitle}>🔧 Debug Info</strong>
          <div className={styles.debugInfoContent}>
            Active layers: <strong>{visibleLayerKeys.length}</strong>
          </div>
          {visibleLayerKeys.length > 12 && (
            <div className={styles.debugInfoWarning}>
              Tip: For best performance, keep fewer than 10–12 layers active at
              once.
            </div>
          )}
          <div className={styles.debugInfoSubtext}>
            Check browser console for loading errors
          </div>
        </div>

        <div className={styles.usageInfo}>
          <strong className={styles.usageInfoTitle}>💡 Usage:</strong>
          <div className={styles.usageInfoContent}>
            • Click map for feature info
            <br />• Add ?lat=43.7&lng=-79.4&radius=500 to URL for spatial
            filtering
          </div>
          {spatialFilter && (
            <div className={styles.spatialFilterActive}>
              🎯 <strong>Spatial filtering active!</strong>
              <br />
              Only showing data within {urlParams.radius}m of target.
            </div>
          )}
        </div>
      </div>

      {/* Map container */}
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        maxZoom={22}
        minZoom={3}
        className={styles.mapContainer}
        zoomAnimation={false}
        fadeAnimation={false}
        markerZoomAnimation={false}
        preferCanvas={true}
        updateWhenIdle={true}
        maxBounds={ONTARIO_BOUNDS}
        maxBoundsViscosity={0.7}
      >
        {/* Basemap - Conditional rendering based on selection */}
        {basemap === "osm" && (
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={25}
            maxNativeZoom={19}
          />
        )}
        {basemap === "google-standard" && (
          <TileLayer
            attribution="&copy; Google"
            url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
            maxZoom={25}
            maxNativeZoom={20}
            subdomains={["mt0", "mt1", "mt2", "mt3"]}
          />
        )}
        {basemap === "google-satellite" && (
          <TileLayer
            attribution="&copy; Google"
            url="https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
            maxZoom={25}
            maxNativeZoom={20}
            subdomains={["mt0", "mt1", "mt2", "mt3"]}
          />
        )}
        {basemap === "google-satellite-no-labels" && (
          <TileLayer
            attribution="&copy; Google"
            url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
            maxZoom={25}
            maxNativeZoom={20}
            subdomains={["mt0", "mt1", "mt2", "mt3"]}
          />
        )}

        {/* Layers whose WMS rendering is broken server-side: render from WFS instead */}
        {LAYERS.map((l) => {
          if (!visible.has(l.key) || !isWMSRenderingBroken(l.key)) return null;

          const styleConfig = LAYER_STYLES[l.style] || LAYER_STYLES.general;

          return (
            <WFSFallbackLayer
              key={l.key}
              features={wfsFallbackData[l.key] || []}
              styleConfig={styleConfig}
            />
          );
        })}

        {/* WMS overlays for each visible layer with spatial filtering */}
        {LAYERS.map((l, idx) => {
          if (!visible.has(l.key) || isWMSRenderingBroken(l.key)) return null;

          // Get style configuration for this layer
          const styleConfig = LAYER_STYLES[l.style] || LAYER_STYLES.general;

          return (
            <WMSTileLayer
              key={l.key}
              url={buildWMSTileLayerUrl(region)}
              layers={l.key}
              format="image/png"
              transparent={true}
              version="1.3.0"
              tiled={true}
              styles=""
              zIndex={200 + idx} // keep overlays above base
              opacity={styleConfig.opacity}
              maxZoom={22}
              maxNativeZoom={defaultZoomLevel}
              tileSize={256}
              // Performance-related options:
              updateWhenZooming={false} // don't constantly refresh tiles mid-zoom
              updateInterval={400} // ms between updates while moving
              keepBuffer={1} // keep fewer off-screen tiles in memory
              // Note: Spatial filtering is applied to GetFeatureInfo queries only,
              // not to tile requests, as CQL filters can break tile caching and
              // may not be supported by all layers
            />
          );
        })}

        {/* Custom GeoJSON Layers */}
        {customLayers.map((layer) => {
          if (!layer.visible) return null;

          return (
            <GeoJSON
              key={layer.id}
              data={layer.data}
              style={{
                color: layer.color,
                weight: 3,
                opacity: 0.8,
                fillColor: layer.color,
                fillOpacity: 0.3,
              }}
              pointToLayer={(_feature, latlng) => {
                return L.circleMarker(latlng, {
                  radius: 6,
                  fillColor: layer.color,
                  color: "#000",
                  weight: 1,
                  opacity: 1,
                  fillOpacity: 0.8,
                });
              }}
              onEachFeature={(feature, layer) => {
                if (feature.properties) {
                  const props = feature.properties;
                  const popupContent = Object.entries(props)
                    .map(([key, value]) => `<b>${key}:</b> ${value}`)
                    .join("<br>");
                  layer.bindPopup(popupContent);
                }
              }}
            />
          );
        })}

        {/* URL-based map controller */}
        <UrlBasedMapController
          urlParams={urlParams}
          spatialFilter={spatialFilter}
          zoomLevel={defaultZoom}
        />

        {/* Search result controller */}
        <SearchResultController
          selectedResult={selectedResult}
          nearbyFeatures={nearbyFeatures}
        />

        {/* Custom AsBuilt File Circles */}
        <CustomAsBuiltCircles
          customAsBuiltFiles={customAsBuiltFiles}
          visible={showCustomAsBuiltLayer}
        />

        {/* Ticket GeoJSON Layer - Renders on top with highest z-index */}
        {showTicketGeoJSON && ticketGeoJSON && (
          <TicketGeoJSONLayer ticketGeoJSON={ticketGeoJSON} />
        )}

        {/* Drawing tools */}
        <DrawControl
          setIsDrawing={setIsDrawing}
          measurementUnit={measurementUnit}
        />

        {/* Click → GetFeatureInfo with spatial filtering */}
        <MapClickInfo
          visibleLayerKeys={wmsQueryableLayerKeys}
          setPopup={setPopup}
          spatialFilter={spatialFilter}
          isDrawing={isDrawing}
          customAsBuiltFiles={customAsBuiltFiles}
          showCustomAsBuiltLayer={showCustomAsBuiltLayer}
          region={region}
        />

        {/* Feature Info Popup */}
        {popup && (
          <FeatureInfoPopup
            popup={popup}
            setPopup={setPopup}
            generateViewerUrl={generateViewerUrl}
            getDrawingIdInfo={getDrawingIdInfo}
          />
        )}
      </MapContainer>
    </div>
  );
}
